import {PropertyTypeConstructor} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {ICoercionUtil} from '@mintdata/mint-kit/api/types/CoercionUtil';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ErrorOrEmpty, IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {BinaryDataCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/BinaryDataCoercionUtil';
import {BooleanCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/BooleanCoercionUtil';
import {ByteCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/ByteCoercionUtil';
import {CurrencyCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/CurrencyCoercionUtil';
import {DateCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/DateCoercionUtil';
import {DateTimeCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/DateTimeCoercionUtil';
import {EmptyCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/EmptyCoercionUtil';
import {ErrorCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/ErrorCoercionUtil';
import {FrameCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/FrameCoercionUtil';
import {IntegerCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/IntegerCoercionUtil';
import {IntegerEnumCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/IntegerEnumCoercionUtil';
import {NumberCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/NumberCoercionUtil';
import {StringCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/StringCoercionUtil';
import {StringEnumCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/StringEnumCoercionUtil';
import {TimeCoercionUtil} from '@mintdata/mint-kit/impl/coercionUtils/TimeCoercionUtil';

export class CoercionUtils
{
    private static booleanUtil:ICoercionUtil = new BooleanCoercionUtil();
    private static byteUtil:ICoercionUtil = new ByteCoercionUtil();
    private static integerUtil:ICoercionUtil = new IntegerCoercionUtil();
    private static numberUtil:ICoercionUtil = new NumberCoercionUtil();
    private static stringUtil:ICoercionUtil = new StringCoercionUtil();

    private static currencyUtil:ICoercionUtil = new CurrencyCoercionUtil();
    private static dateUtil:ICoercionUtil = new DateCoercionUtil();
    private static dateTimeUtil:ICoercionUtil = new DateTimeCoercionUtil();
    private static timeUtil:ICoercionUtil = new TimeCoercionUtil();

    private static stringEnumUtil:ICoercionUtil = new StringEnumCoercionUtil();
    private static integerEnumUtil:ICoercionUtil = new IntegerEnumCoercionUtil();
    private static binaryData:ICoercionUtil = new BinaryDataCoercionUtil();

    private static frameUtil:ICoercionUtil = new FrameCoercionUtil();
    private static emptyUtil:ICoercionUtil = new EmptyCoercionUtil();
    private static errorUtil:ICoercionUtil = new ErrorCoercionUtil();

    private static getUtil(from:IValue):ICoercionUtil
    {
        const origType = from.getType();
        switch( true )
        {
            case origType.isEqualTo(Type.Boolean):
                return this.booleanUtil;
            case origType.isEqualTo(Type.Byte):
                return this.byteUtil;
            case origType.isEqualTo(Type.Integer):
                return this.integerUtil;
            case origType.isEqualTo(Type.Number):
                return this.numberUtil;
            case origType.isEqualTo(Type.String):
                return this.stringUtil;

            case origType.isEqualTo(Type.Currency):
                return this.currencyUtil;
            case origType.isEqualTo(Type.Date):
                return this.dateUtil;
            case origType.isEqualTo(Type.DateTime):
                return this.dateTimeUtil;
            case origType.isEqualTo(Type.Time):
                return this.timeUtil;

            case origType.isEqualTo(Type.StringEnum):
                return this.stringEnumUtil;
            case origType.isEqualTo(Type.IntegerEnum):
                return this.integerEnumUtil;
            case origType.isEqualTo(Type.BinaryData):
                return this.binaryData;

            case origType.isEqualTo(Type.Frame):
                return this.frameUtil;

            case origType.isEqualTo(Type.Empty):
                return this.emptyUtil;

            case origType.isEqualTo(Type.Error):
                return this.errorUtil;
        }
    }

    public static coerce(from:IValue, to:Type | null, toCtor:PropertyTypeConstructor<any>):IValue | ErrorOrEmpty
    {
        if( !from )
            return AEmpty.Value;

        const fromType = from.getType();
        const util:ICoercionUtil = CoercionUtils.getUtil(from);

        try
        {
            switch( true )
            {
                case to === null:
                {
                    /**
                     * Last resort coercion.
                     * If a component's author uses properties other than IValue (e.g. Units),
                     * we allow the user to take care of coercion by defining `coerce`
                     * method on a typeCtor
                     */
                    return util.toCustom(from, toCtor);
                }
                case to.isEqualTo(fromType):
                    return from;
                case to.isEqualTo(Type.Empty):
                    return AEmpty.Value;
                case to.isEqualTo(Type.Boolean):
                    return util.toBoolean(from);
                case to.isEqualTo(Type.Byte):
                    return util.toByte(from);
                case to.isEqualTo(Type.Integer):
                    return util.toInteger(from);
                case to.isEqualTo(Type.Number):
                    return util.toNumber(from);
                case to.isEqualTo(Type.String):
                    return util.toString(from);

                case to.isEqualTo(Type.Currency):
                    return util.toCurrency(from);
                case to.isEqualTo(Type.Date):
                    return util.toDate(from);
                case to.isEqualTo(Type.DateTime):
                    return util.toDateTime(from);
                case to.isEqualTo(Type.Time):
                    return util.toTime(from);

                case to.isEqualTo(Type.StringEnum):
                    return util.toStringEnum(from, toCtor);
                case to.isEqualTo(Type.IntegerEnum):
                    return util.toIntegerEnum(from, toCtor);
                case to.isEqualTo(Type.BinaryData):
                    return util.toBinaryData(from);

                case to.isEqualTo(Type.Frame):
                    return util.toFrame(from);

                default:
                    console.warn(`expected type isn\'t found for: from=${fromType}, to=${to}`);
            }
        }
        catch( e )
        {
            console.error(`error while converting: from=${fromType}, to=${to}: `, e);
            return EvalErrors.Value;
        }
    }

    public static defaultValue(type:Type | null, toCtor:PropertyTypeConstructor<any>):IValue | ErrorOrEmpty
    {
        return CoercionUtils.coerce(AEmpty.Value, type, toCtor);
    }
}
